import { b2x } from '@b2x/react/src';
import React from 'react';

export interface SalesforceChatProps {
  deploymentName?: string;
  orgId?: string;
  preChatDefaultCountry?: string;
  preChatDefaultEmail?: string;
  preChatDefaultFirstName?: string;
  preChatDefaultLanguage?: string;
  preChatDefaultLastName?: string;
  preChatDefaultPhone?: string;
  preChatDefaultSubject?: string;
  scrt2URL?: string;
  siteUrl?: string;
  snippetLanguage?: string;
}

interface PreChatField {
  isEditableByEndUser: boolean;
  value: string;
}

interface PreChatFields {
  Phone: PreChatField;
  _email: PreChatField;
  _firstName: PreChatField;
  _lastName: PreChatField;
  _subject: PreChatField;
}

interface HiddenPreChatFields {
  Country: string;
  Language: string;
}

interface EmbeddedMessagingConfig {
  scrt2URL: string;
}

// Extend Window interface to include Salesforce globals
declare global {
  interface Window {
    availableCountriesSFMessaging?: Array<string>;
    availableLanguagesSFMessaging?: Array<string>;
    embeddedservice_bootstrap?: {
      init(orgId: string, deploymentName: string, siteUrl: string, config: EmbeddedMessagingConfig): void;
      prechatAPI: {
        setHiddenPrechatFields(fields: HiddenPreChatFields): void;
        setVisiblePrechatFields(fields: PreChatFields): void;
      };
      settings: {
        language: string;
      };
    };
  }
}

export const SalesforceChat = ({
  deploymentName = 'IT_MIAW_Purinashop',
  orgId = '00D0900000460T3',
  preChatDefaultCountry = 'IT',
  preChatDefaultEmail = '',
  preChatDefaultFirstName = '',

  // Default pre-chat values
  preChatDefaultLanguage = 'it',
  preChatDefaultLastName = '',
  preChatDefaultPhone = '',
  preChatDefaultSubject = '',
  scrt2URL = 'https://nestlecesomni.my.salesforce-scrt.com',
  siteUrl = 'https://nestlecesomni.my.site.com/ESWITMIAWPurinashop1753967965786',
  snippetLanguage = 'it', // ENGAGE_TEAM - Set the default language for the Pre Chat
}: SalesforceChatProps) => {
  // Initialize embedded messaging
  const initEmbeddedMessaging = React.useCallback(() => {
    /* Method to return the preferred language */
    const pickLanguage = (
      listLang: Array<string> | undefined,
      preferredLang: string,
      consumerLang: string,
      snippetLang: string
    ): string => {
      let chosenLanguage = snippetLang;

      if (preferredLang && listLang && listLang.indexOf(preferredLang) !== -1) {
        chosenLanguage = preferredLang;
      } else if (consumerLang && listLang && listLang.indexOf(consumerLang) !== -1) {
        chosenLanguage = consumerLang;
      } else if (
        consumerLang &&
        listLang &&
        consumerLang.indexOf('-') !== -1 &&
        listLang.indexOf(consumerLang.replace('-', '_')) !== -1
      ) {
        chosenLanguage = consumerLang.replace('-', '_');
      } else if (
        consumerLang &&
        listLang &&
        consumerLang.indexOf('-') !== -1 &&
        listLang.indexOf(consumerLang.split('-')[0]) !== -1
      ) {
        chosenLanguage = consumerLang.split('-')[0];
      }
      return chosenLanguage;
    };

    /* Method to return the preferred country */
    const pickCountry = (listCnty: Array<string> | undefined, preferredCnty: string, consumerCnty: string): string => {
      let chosenCountry = '';

      if (preferredCnty && listCnty && listCnty.indexOf(preferredCnty) !== -1) {
        chosenCountry = preferredCnty;
      } else if (consumerCnty && listCnty && listCnty.indexOf(consumerCnty) !== -1) {
        chosenCountry = consumerCnty;
      }
      return chosenCountry;
    };

    try {
      const availableLanguages = window.availableLanguagesSFMessaging || [];
      const availableCountries = window.availableCountriesSFMessaging || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const consumerLanguage = navigator.language || (navigator as any).userLanguage;
      const consumerCountry = consumerLanguage.indexOf('-') !== -1 ? consumerLanguage.split('-')[1] : '';

      console.log('Live Chat - initEmbeddedMessaging');

      const finalDefaultLanguage = pickLanguage(
        availableLanguages,
        preChatDefaultLanguage,
        consumerLanguage,
        snippetLanguage
      );
      const finalDefaultCountry = pickCountry(availableCountries, preChatDefaultCountry, consumerCountry);

      if (window.embeddedservice_bootstrap) {
        window.embeddedservice_bootstrap.settings.language = finalDefaultLanguage;

        window.addEventListener('onEmbeddedMessagingReady', () => {
          // ENGAGE_TEAM - Set which fields should be hidden and visible. This section should be aligned to Embedded Service configuration.

          if (window.embeddedservice_bootstrap?.prechatAPI) {
            // Set hidden prechat fields
            window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
              Country: finalDefaultCountry,
              Language: finalDefaultLanguage,
            });

            // Set visible prechat fields
            window.embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields({
              Phone: {
                isEditableByEndUser: true,
                value: preChatDefaultPhone,
              },
              _email: {
                isEditableByEndUser: true,
                value: preChatDefaultEmail,
              },
              _firstName: {
                isEditableByEndUser: true,
                value: preChatDefaultFirstName,
              },
              _lastName: {
                isEditableByEndUser: true,
                value: preChatDefaultLastName,
              },
              _subject: {
                isEditableByEndUser: true,
                value: preChatDefaultSubject,
              },
            });
          }
        });

        // Initialize embedded messaging
        window.embeddedservice_bootstrap.init(orgId, deploymentName, siteUrl, {
          scrt2URL: scrt2URL,
        });
      }
    } catch (err) {
      console.error('Error loading Embedded Messaging: ', err);
    }
  }, [
    deploymentName,
    orgId,
    preChatDefaultCountry,
    preChatDefaultEmail,
    preChatDefaultFirstName,
    preChatDefaultLanguage,
    preChatDefaultLastName,
    preChatDefaultPhone,
    preChatDefaultSubject,
    scrt2URL,
    siteUrl,
    snippetLanguage,
  ]);

  React.useEffect(() => {
    // Load the available languages script
    b2x.addScript({
      id: 'salesforce-available-languages',
      src: 'https://nestlecesomni.my.salesforce-sites.com/liveAgentSetupFlow/resource/1751992624000/AvailableLanguagesSFMessaging',
    });
    // Load the available countries script
    b2x.addScript({
      id: 'salesforce-available-countries',
      src: 'https://nestlecesomni.my.salesforce-sites.com/liveAgentSetupFlow/resource/1751992624000/AvailableCountriesSFMessaging',
    });
    // Load the bootstrap script
    b2x
      .addScript({
        id: 'salesforce-bootstrap',
        src: 'https://nestlecesomni.my.site.com/ESWITMIAWPurinashop1753967965786/assets/js/bootstrap.min.js',
      })
      .finally(() => {
        // Initialize embedded messaging after scripts are loaded
        initEmbeddedMessaging();
      });
  }, [initEmbeddedMessaging]);

  return <></>;
};
